import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './css/RecipesList.css';
import './css/CreatorDashboard.css';
import { motion } from 'framer-motion';
import { creators } from './config/creators';
import { updateRecipeUrl, AUTH_SIGNOUT_URL } from './config/apiConfig';
import SEO from './components/SEO';

const SESSION_KEY = 'savory_creator';

function getCreatorSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw);
    return session && session.email ? session : null;
  } catch {
    return null;
  }
}

function CreatorDashboard() {
  const navigate = useNavigate();
  const [creatorInfo, setCreatorInfo] = useState(null);
  const [contentCreator, setContentCreator] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const observer = useRef();
  const loadingRef = useRef(null);

  const RECIPES_PER_PAGE = 12;

  const lastRecipeElementRef = useCallback(node => {
    if (loading || loadingMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) loadMoreRecipes();
    }, { threshold: 0.5 });
    if (node) observer.current.observe(node);
  }, [loading, loadingMore, hasMore]);

  const fetchContentCreatorInfo = useCallback(async (creatorId) => {
    try {
      const response = await fetch(`https://starfish-app-9uavi.ondigitalocean.app/content-creators/${creatorId}`);
      if (!response.ok) return;
      const data = await response.json();
      setContentCreator(data);
    } catch (err) {
      console.error('Error fetching content creator info:', err);
    }
  }, []);

  const fetchCreatorRecipes = useCallback(async (creatorId, page, resetList = false) => {
    try {
      resetList ? setLoading(true) : setLoadingMore(true);
      setError(null);
      const response = await fetch(`https://starfish-app-9uavi.ondigitalocean.app/content-creators/${creatorId}/recipes?page=${page}&limit=${RECIPES_PER_PAGE}`);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      console.log(`Fetched creator recipes for page ${page}:`, data);
      let newRecipes = [];
      if (data && data.recipes && Array.isArray(data.recipes)) {
        newRecipes = data.recipes;
      } else if (Array.isArray(data)) {
        newRecipes = data;
      } else {
        newRecipes = [];
      }
      if (data.pagination) {
        setHasMore(data.pagination.hasMore || page < data.pagination.totalPages);
      } else {
        setHasMore(newRecipes.length === RECIPES_PER_PAGE);
      }
      if (resetList) setRecipes(newRecipes);
      else setRecipes(prev => [...prev, ...newRecipes]);
    } catch (err) {
      console.error('Error fetching creator recipes:', err);
      setError('Failed to load recipes. Please try again.');
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    console.log('📱 Creator Dashboard - useEffect triggered');
    
    const session = getCreatorSession();
    if (!session) {
      console.log('❌ No session found, redirecting to login');
      navigate('/creators/login');
      return;
    }
    
    console.log('📱 Creator Dashboard - Session loaded:');
    console.log('  - Email:', session.email);
    console.log('  - User ID (Supabase):', session.userId || session.user_id);
    console.log('  - Creator ID:', session.creator_id || session.CreatorId);
    console.log('  - Access Token:', session.access_token ? '✓ Present' : '✗ Missing');
    
    // Get creator_id from session (comes from backend)
    const creatorId = session.creator_id || session.CreatorId;
    
    if (!creatorId) {
      console.error('❌ Creator ID not found in session');
      console.log('📋 Session data:', session);
      setError('Creator ID not found. Your account needs to be set up as a creator.');
      setLoading(false);
      return;
    }
    
    console.log('✅ Using Creator ID from session:', creatorId);
    
    // Try to find creator info from creators.js for additional metadata (optional)
    const creatorFromFile = creators.find(c => c.email.toLowerCase() === session.email.toLowerCase());
    
    const creatorInfo = {
      email: session.email,
      userId: session.userId || session.user_id,
      CreatorId: creatorId,
      creator_id: creatorId,
      ...(creatorFromFile || {}) // Merge any additional info from creators.js if available
    };
    
    setCreatorInfo(creatorInfo);
    fetchContentCreatorInfo(creatorId);
    fetchCreatorRecipes(creatorId, 1, true);
  }, [navigate, fetchCreatorRecipes, fetchContentCreatorInfo]);

  const loadMoreRecipes = useCallback(() => {
    if (!hasMore || loading || loadingMore || !creatorInfo?.CreatorId) return;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchCreatorRecipes(creatorInfo.CreatorId, nextPage);
  }, [hasMore, loading, loadingMore, creatorInfo, currentPage, fetchCreatorRecipes]);

  const handleLogout = async () => {
    try {
      const session = getCreatorSession();
      if (session?.access_token) {
        // Call signout endpoint
        await fetch(AUTH_SIGNOUT_URL, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
          },
        }).catch(() => {
          // Ignore errors, still log out locally
        });
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem(SESSION_KEY);
      navigate('/creators/login');
    }
  };

  const handleDeleteRecipe = async (e, recipe) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Delete "${recipe.name}"? It will be hidden from the feed. You can edit it later to make it visible again.`)) return;
    setDeletingId(recipe.id);
    try {
      const res = await fetch(updateRecipeUrl(recipe.id), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visibility: false }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || data.message || `Failed (${res.status})`);
      }
      setRecipes((prev) => prev.filter((r) => r.id !== recipe.id));
    } catch (err) {
      setError(err.message || 'Failed to delete recipe.');
    } finally {
      setDeletingId(null);
    }
  };

  const SkeletonRecipeCard = () => (
    <div className="recipe-card skeleton-card">
      <div className="skeleton-image"></div>
      <div className="skeleton-content">
        <div className="skeleton-title"></div>
        <div className="skeleton-stats">
          <div className="skeleton-stat"></div>
          <div className="skeleton-stat"></div>
        </div>
      </div>
    </div>
  );

  const renderSkeletonGrid = () => (
    <div className="recipes-grid">
      {[...Array(12)].map((_, index) => (
        <SkeletonRecipeCard key={`skeleton-grid-${index}`} />
      ))}
    </div>
  );

  if (!creatorInfo) {
    return (
      <div className="recipes-list-container">
        <div className="loading-message">Loading...</div>
      </div>
    );
  }

  return (
    <div className="recipes-list-container">
      <SEO
        title={`Creator Dashboard - ${creatorInfo.email} | Savory`}
        description="Manage your recipes and creator profile"
        keywords="creator dashboard, recipes, cooking"
        url="/creators/dashboard"
      />
      <div className="creator-dashboard-logout-wrap">
        <button type="button" onClick={handleLogout} className="creator-dashboard-logout">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          <span>Logout</span>
        </button>
      </div>

      <motion.div
        className="content-creator-header"
        style={{
          backgroundImage: contentCreator?.banner_picture_url
            ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${contentCreator.banner_picture_url})`
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="content-creator-info creator-dashboard-header-info">
          {contentCreator ? (
            <>
              {contentCreator.profile_picture_url && (
                <motion.div
                  className="content-creator-avatar"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <img
                    src={contentCreator.profile_picture_url}
                    alt={contentCreator.name}
                    className="content-creator-profile-pic"
                  />
                </motion.div>
              )}
              <h1 className="content-creator-name">{contentCreator.name}</h1>
              {contentCreator.description && (
                <p className="content-creator-description">{contentCreator.description}</p>
              )}
              <p className="content-creator-subtitle">Creator Dashboard</p>
              <Link to="/recipe-add" className="creator-add-recipe-btn-hero">
                <span className="creator-add-recipe-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                Add Recipe
              </Link>
            </>
          ) : (
            <>
              <div className="content-creator-avatar">
                <div className="content-creator-profile-pic skeleton-creator-profile"></div>
              </div>
              <div className="content-creator-name skeleton-creator-title"></div>
              <div className="content-creator-description skeleton-creator-description"></div>
              <div className="content-creator-subtitle skeleton-creator-subtitle"></div>
            </>
          )}
        </div>
      </motion.div>

      <div className="TrendingFilter">
        <div className="inner-container">
          <div className="header2">
            <h1 className="titleRecipes">Your Recipes</h1>
            {recipes.length > 0 && (
              <span className="recipe-count">{recipes.length} recipe{recipes.length !== 1 ? 's' : ''}</span>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => fetchCreatorRecipes(creatorInfo.CreatorId, 1, true)} className="retry-button">
            Try Again
          </button>
        </div>
      )}

      {loading ? (
        renderSkeletonGrid()
      ) : (
        <>
          {recipes.length === 0 ? (
            <div className="no-recipes-found">
              <h2>No recipes yet</h2>
              <p>Start sharing your recipes with the Savory community!</p>
              <Link to="/recipe-add" className="creator-add-recipe-btn-large">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 5V19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Add Your First Recipe
              </Link>
            </div>
          ) : (
            <motion.div
              className="recipes-grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
            >
              {recipes.map((recipe, index) => (
                <motion.div
                  key={recipe.id}
                  className="recipe-card creator-dashboard-recipe-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: Math.min(index * 0.05, 0.5), ease: "easeOut" }}
                  whileHover={{ y: -5, scale: 1.03, boxShadow: "0 12px 20px rgba(0,0,0,0.08)" }}
                  whileTap={{ scale: 0.98 }}
                  ref={index === recipes.length - 1 ? lastRecipeElementRef : null}
                >
                  <Link to={`/recipe-details/${recipe.id}`}>
                    <motion.div className="recipe-image-wrapper" whileHover={{ scale: 1.02 }} transition={{ duration: 0.2, ease: "easeOut" }}>
                      <motion.img
                        src={recipe.url || 'https://via.placeholder.com/300x200?text=No+Image'}
                        alt={recipe.name}
                        className="recipe-image"
                        initial={{ scale: 1.0 }}
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/300x200?text=Image+Error';
                        }}
                      />
                    </motion.div>
                    <motion.div className="recipe-content" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15, delay: 0.1 }}>
                      <motion.h2 whileHover={{ color: "#FF6B6B" }} transition={{ duration: 0.15 }}>
                        {recipe.name}
                      </motion.h2>
                      <motion.div className="recipe-stats" initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.15, delay: 0.15 }}>
                        <p className="calories"><strong>Calories:</strong> {recipe.calories || 'N/A'}</p>
                        <p className="proteins"><strong>Proteins:</strong> {recipe.proteins || 'N/A'} g</p>
                      </motion.div>
                    </motion.div>
                  </Link>
                  <Link
                    to={`/recipe-add/${recipe.id}`}
                    className="creator-dashboard-edit-btn"
                    onClick={(e) => e.stopPropagation()}
                    aria-label="Edit recipe"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                    <span className="creator-dashboard-edit-text">Edit</span>
                  </Link>
                  <button
                    type="button"
                    className="creator-dashboard-delete-btn"
                    onClick={(e) => handleDeleteRecipe(e, recipe)}
                    disabled={deletingId === recipe.id}
                    aria-label="Delete recipe (hide from feed)"
                  >
                    {deletingId === recipe.id ? (
                      <span className="creator-dashboard-delete-spinner" aria-hidden="true" />
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        <line x1="10" y1="11" x2="10" y2="17"/>
                        <line x1="14" y1="11" x2="14" y2="17"/>
                      </svg>
                    )}
                    <span className="creator-dashboard-edit-text">Delete</span>
                  </button>
                </motion.div>
              ))}
            </motion.div>
          )}
        </>
      )}

      {!loading && recipes.length > 0 && (
        <div className="loading-more" ref={loadingRef}>
          {loadingMore && <div className="loading-spinner-small"></div>}
          {!hasMore && recipes.length > RECIPES_PER_PAGE && (
            <p className="no-more-recipes">No more recipes to load</p>
          )}
        </div>
      )}
    </div>
  );
}

export default CreatorDashboard;
